import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInventory } from '../../context/InventoryContext';
import { useAuth } from '../../context/AuthContext';
import Input from '../ui/Input';
import Select from '../ui/Select';
import Button from '../ui/Button';
import { Card, CardHeader, CardTitle, CardContent, CardFooter } from '../ui/Card';
import { Trash, Camera, X, Upload, Eye } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface RDWasteItem {
  id: string;
  item_id: string;
  original_project_name: string;
  description: string;
  category: string;
  category_id: string;
  date_added: string;
  reason: string;
  original_cost: number;
  disposal_status: string;
  salvage_value: number;
  researcher: string;
  project_team: string;
  storage_location: string;
  project_code: string;
  disposal_method: string;
  environmental_impact: string;
  disposal_cost?: number;
  images?: string[];
}

interface RDWasteFormProps {
  item?: RDWasteItem;
  isEdit?: boolean;
}

const RDWasteForm: React.FC<RDWasteFormProps> = ({ 
  item,
  isEdit = false
}) => {
  const navigate = useNavigate();
  const { categories } = useInventory();
  const { user } = useAuth();

  const [formData, setFormData] = useState({
    item_id: '',
    original_project_name: '',
    description: '',
    category: 'other',
    category_id: '',
    date_added: new Date().toISOString().split('T')[0],
    reason: '',
    original_cost: 0,
    disposal_status: 'pending',
    salvage_value: 0,
    researcher: '',
    project_team: '',
    storage_location: '',
    project_code: '',
    disposal_method: 'standard_recycling',
    environmental_impact: 'low',
    disposal_cost: 0,
  });
  
  const [images, setImages] = useState<string[]>([]);
  const [uploadingImages, setUploadingImages] = useState<boolean[]>([]);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [showImageModal, setShowImageModal] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (item && isEdit) {
      setFormData({
        item_id: item.item_id,
        original_project_name: item.original_project_name,
        description: item.description || '',
        category: item.category,
        category_id: item.category_id || '',
        date_added: item.date_added,
        reason: item.reason || '',
        original_cost: item.original_cost,
        disposal_status: item.disposal_status,
        salvage_value: item.salvage_value,
        researcher: item.researcher,
        project_team: item.project_team || '',
        storage_location: item.storage_location || '',
        project_code: item.project_code,
        disposal_method: item.disposal_method,
        environmental_impact: item.environmental_impact,
        disposal_cost: item.disposal_cost || 0,
      });
      setImages(item.images || []);
    } else if (!isEdit) {
      // Generate new item ID for new items
      const timestamp = Date.now().toString().slice(-6);
      const random = Math.floor(Math.random() * 1000).toString().padStart(3, '0');
      setFormData(prev => ({
        ...prev,
        item_id: `RDW-${new Date().getFullYear()}-${timestamp}-${random}`
      }));
    }
  }, [item, isEdit]);

  const compressImage = (file: File, maxWidth: number = 1200, quality: number = 0.8): Promise<Blob> => {
    return new Promise((resolve) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d')!;
      const img = new Image();
      
      img.onload = () => {
        // Calculate new dimensions
        const ratio = Math.min(maxWidth / img.width, maxWidth / img.height);
        canvas.width = img.width * ratio;
        canvas.height = img.height * ratio;
        
        // Draw and compress
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(resolve, 'image/jpeg', quality);
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = async (files: FileList) => {
    const maxFileSize = 5 * 1024 * 1024; // 5MB
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    
    for (let i = 0; i < files.length; i++) {
      const file = files[i];
      
      // Validate file type
      if (!allowedTypes.includes(file.type)) {
        setErrors(prev => ({
          ...prev,
          images: `File ${file.name} is not a supported image format`
        }));
        continue;
      }
      
      // Validate file size
      if (file.size > maxFileSize) {
        setErrors(prev => ({
          ...prev,
          images: `File ${file.name} exceeds 5MB limit`
        }));
        continue;
      }
      
      try {
        setUploadingImages(prev => [...prev, true]);
        
        // Compress image
        const compressedBlob = await compressImage(file);
        const compressedFile = new File([compressedBlob!], file.name, {
          type: 'image/jpeg',
          lastModified: Date.now(),
        });
        
        // Upload to Supabase Storage
        const fileExt = 'jpg'; // Always use jpg after compression
        const fileName = `${formData.item_id}-${Date.now()}-${i}.${fileExt}`;
        const filePath = `rd-waste-images/${fileName}`;
        
        const { error: uploadError } = await supabase.storage
          .from('inventory')
          .upload(filePath, compressedFile);
        
        if (uploadError) throw uploadError;
        
        // Get public URL
        const { data: { publicUrl } } = supabase.storage
          .from('inventory')
          .getPublicUrl(filePath);
        
        setImages(prev => [...prev, publicUrl]);
        setErrors(prev => ({ ...prev, images: '' }));
      } catch (error) {
        console.error('Error uploading image:', error);
        setErrors(prev => ({
          ...prev,
          images: error.message || `Failed to upload ${file.name}`
        }));
      } finally {
        setUploadingImages(prev => prev.slice(0, -1));
      }
    }
  };

  const handleRemoveImage = async (imageUrl: string, index: number) => {
    try {
      // Extract file path from URL
      const urlParts = imageUrl.split('/');
      const fileName = urlParts[urlParts.length - 1];
      const filePath = `rd-waste-images/${fileName}`;
      
      // Remove from Supabase Storage
      const { error } = await supabase.storage
        .from('inventory')
        .remove([filePath]);
      
      if (error) throw error;
      
      // Remove from local state
      setImages(prev => prev.filter((_, i) => i !== index));
    } catch (error) {
      console.error('Error removing image:', error);
      setErrors(prev => ({
        ...prev,
        images: error.message || 'Failed to remove image'
      }));
    }
  };
  const validateForm = () => {
    const newErrors: Record<string, string> = {};
    
    if (!formData.original_project_name.trim()) {
      newErrors.original_project_name = 'Project name is required';
    }
    
    if (!formData.item_id.trim()) {
      newErrors.item_id = 'Item ID is required';
    }
    
    if (!formData.reason.trim()) {
      newErrors.reason = 'Reason for waste classification is required';
    }
    
    if (!formData.researcher.trim()) {
      newErrors.researcher = 'Researcher is required';
    }
    
    if (!formData.project_code.trim()) {
      newErrors.project_code = 'Project code is required';
    }
    
    if (formData.original_cost < 0) {
      newErrors.original_cost = 'Original cost cannot be negative';
    }
    
    if (formData.salvage_value < 0) {
      newErrors.salvage_value = 'Salvage value cannot be negative';
    }

    if (formData.disposal_cost < 0) {
      newErrors.disposal_cost = 'Disposal cost cannot be negative';
    }

    if (formData.salvage_value > formData.original_cost) {
      newErrors.salvage_value = 'Salvage value cannot exceed original cost';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    const numValue = parseFloat(value) || 0;
    setFormData(prev => ({ ...prev, [name]: numValue }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSelectChange = (name: string) => (value: string) => {
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }));
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsSubmitting(true);
    setErrors({});
    
    try {
      const wasteItemData = {
        ...formData,
        user_id: user?.id,
        category_id: formData.category_id || null,
        images
      };

      if (isEdit && item) {
        const { error } = await supabase
          .from('rd_waste_items')
          .update(wasteItemData)
          .eq('id', item.id);

        if (error) throw error;
      } else {
        const { error } = await supabase
          .from('rd_waste_items')
          .insert([wasteItemData]);

        if (error) throw error;
      }
      
      navigate('/rd-waste');
    } catch (error: any) {
      console.error('Error saving R&D waste item:', error);
      setErrors(prev => ({
        ...prev,
        submit: error.message || 'Failed to save waste item. Please try again.'
      }));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteItem = () => {
    if (!item) return;
    
    if (window.confirm('Are you sure you want to delete this waste item? This action cannot be undone.')) {
      deleteWasteItem();
    }
  };

  const deleteWasteItem = async () => {
    if (!item) return;

    try {
      const { error } = await supabase
        .from('rd_waste_items')
        .delete()
        .eq('id', item.id);

      if (error) throw error;
      navigate('/rd-waste');
    } catch (error) {
      console.error('Error deleting waste item:', error);
      setErrors(prev => ({
        ...prev,
        submit: 'Failed to delete waste item. Please try again.'
      }));
    }
  };

  const categoryOptions = [
    { value: '', label: 'Uncategorized' },
    ...categories.map(category => ({
      value: category.id,
      label: category.name,
    }))
  ];

  const wasteTypeOptions = [
    { value: 'chemical', label: 'Chemical' },
    { value: 'biological', label: 'Biological' },
    { value: 'electronic', label: 'Electronic' },
    { value: 'mechanical', label: 'Mechanical' },
    { value: 'hazardous', label: 'Hazardous' },
    { value: 'non_hazardous', label: 'Non-Hazardous' },
    { value: 'other', label: 'Other' },
  ];

  const disposalStatusOptions = [
    { value: 'pending', label: 'Pending' },
    { value: 'scheduled', label: 'Scheduled' },
    { value: 'completed', label: 'Completed' },
  ];

  const disposalMethodOptions = [
    { value: 'standard_recycling', label: 'Standard Recycling' },
    { value: 'electronics_recycling', label: 'Electronics Recycling' },
    { value: 'specialized_recycling', label: 'Specialized Recycling' },
    { value: 'hazmat_disposal', label: 'Hazmat Disposal' },
    { value: 'component_salvage', label: 'Component Salvage' },
    { value: 'secure_destruction', label: 'Secure Destruction' },
  ];

  const environmentalImpactOptions = [
    { value: 'minimal', label: 'Minimal' },
    { value: 'low', label: 'Low' },
    { value: 'moderate', label: 'Moderate' },
    { value: 'high', label: 'High' },
  ];

  return (
    <Card className="max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex justify-between items-center">
          <CardTitle>{isEdit ? 'Edit Waste Item' : 'Add New Waste Item'}</CardTitle>
          {isEdit && item && (
            <Button
              type="button"
              variant="danger"
              onClick={handleDeleteItem}
            >
              <Trash className="h-4 w-4 mr-2" />
              Delete Waste Item
            </Button>
          )}
        </div>
      </CardHeader>
      <form onSubmit={handleSubmit}>
        <CardContent className="space-y-6">
          {/* Basic Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Basic Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Item ID"
                name="item_id"
                value={formData.item_id}
                onChange={handleInputChange}
                error={errors.item_id}
                fullWidth
                required
                disabled={isEdit}
              />
              
              <Input
                label="Original Project Name"
                name="original_project_name"
                value={formData.original_project_name}
                onChange={handleInputChange}
                error={errors.original_project_name}
                fullWidth
                required
              />
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Description
              </label>
              <textarea
                name="description"
                rows={3}
                value={formData.description}
                onChange={handleInputChange}
                className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900"
                placeholder="Detailed description of the item and its condition"
              />
            </div>
          </div>

          {/* Image Upload Section */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Documentation Photos</h3>
            
            {/* Upload Area */}
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center hover:border-gray-400 transition-colors">
              <input
                type="file"
                multiple
                accept="image/jpeg,image/png,image/gif,image/webp"
                onChange={(e) => e.target.files && handleImageUpload(e.target.files)}
                className="hidden"
                id="image-upload"
              />
              <label htmlFor="image-upload" className="cursor-pointer">
                <Camera className="h-12 w-12 text-gray-600 mx-auto mb-4" />
                <p className="text-lg font-medium text-gray-900 mb-2">Upload Images</p>
                <p className="text-sm text-gray-500 mb-4">
                  Drag and drop images here, or click to select files
                </p>
                <Button type="button" variant="secondary">
                  <Upload className="h-4 w-4 mr-2" />
                  Choose Files
                </Button>
              </label>
            </div>
            
            <p className="text-xs text-gray-500 mt-2">
              Supported formats: JPEG, PNG, GIF, WebP. Maximum file size: 5MB per image.
              Images will be automatically compressed for optimal storage.
            </p>
            
            {errors.images && (
              <p className="mt-2 text-sm text-red-600">{errors.images}</p>
            )}
            
            {/* Image Preview Grid */}
            {images.length > 0 && (
              <div className="mt-6">
                <h4 className="text-sm font-medium text-gray-900 mb-3">
                  Uploaded Images ({images.length})
                </h4>
                <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                  {images.map((imageUrl, index) => (
                    <div key={index} className="relative group">
                      <div className="aspect-square bg-gray-100 rounded-lg overflow-hidden">
                        <img
                          src={imageUrl}
                          alt={`Waste item photo ${index + 1}`}
                          className="w-full h-full object-cover cursor-pointer hover:opacity-75 transition-opacity"
                          onClick={() => {
                            setImagePreview(imageUrl);
                            setShowImageModal(true);
                          }}
                        />
                      </div>
                      
                      {/* Image Controls */}
                      <div className="absolute top-2 right-2 flex space-x-1 opacity-0 group-hover:opacity-100 transition-opacity">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => {
                            setImagePreview(imageUrl);
                            setShowImageModal(true);
                          }}
                          className="p-1"
                        >
                          <Eye className="h-3 w-3" />
                        </Button>
                        <Button
                          type="button"
                          variant="danger"
                          size="sm"
                          onClick={() => handleRemoveImage(imageUrl, index)}
                          className="p-1"
                        >
                          <X className="h-3 w-3" />
                        </Button>
                      </div>
                      
                      {/* Upload Progress */}
                      {uploadingImages[index] && (
                        <div className="absolute inset-0 bg-black bg-opacity-50 flex items-center justify-center rounded-lg">
                          <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-white"></div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
          {/* Classification */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Classification</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Waste Category"
                options={wasteTypeOptions}
                value={formData.category}
                onChange={handleSelectChange('category')}
                error={errors.category}
                fullWidth
                required
              />
              
              <Select
                label="Product Category"
                options={categoryOptions}
                value={formData.category_id}
                onChange={handleSelectChange('category_id')}
                error={errors.category_id}
                fullWidth
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label="Date Added to Waste"
                name="date_added"
                type="date"
                value={formData.date_added}
                onChange={handleInputChange}
                fullWidth
                required
              />
              
              <Select
                label="Disposal Status"
                options={disposalStatusOptions}
                value={formData.disposal_status}
                onChange={handleSelectChange('disposal_status')}
                fullWidth
                required
              />
            </div>

            <div className="mt-4">
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Reason for Waste Classification *
              </label>
              <textarea
                name="reason"
                rows={3}
                value={formData.reason}
                onChange={handleInputChange}
                className={`w-full rounded-md border ${
                  errors.reason ? 'border-red-500' : 'border-gray-300'
                } px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-900 focus:border-blue-900`}
                placeholder="Detailed explanation of why this item is classified as waste"
                required
              />
              {errors.reason && (
                <p className="mt-1 text-xs text-red-500">{errors.reason}</p>
              )}
            </div>
          </div>

          {/* Project Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Project Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Input
                label="Project Code"
                name="project_code"
                value={formData.project_code}
                onChange={handleInputChange}
                error={errors.project_code}
                placeholder="e.g., RD-2024-001"
                fullWidth
                required
              />
              
              <Input
                label="Researcher"
                name="researcher"
                value={formData.researcher}
                onChange={handleInputChange}
                error={errors.researcher}
                placeholder="e.g., Dr. John Smith"
                fullWidth
                required
              />
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
              <Input
                label="Project Team"
                name="project_team"
                value={formData.project_team}
                onChange={handleInputChange}
                placeholder="e.g., Quantum Computing Division"
                fullWidth
              />
              
              <Input
                label="Storage Location"
                name="storage_location"
                value={formData.storage_location}
                onChange={handleInputChange}
                placeholder="e.g., R&D Waste Storage - Bay 1"
                fullWidth
              />
            </div>
          </div>

          {/* Financial Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Financial Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <Input
                label="Original Cost ($)"
                name="original_cost"
                type="number"
                min="0"
                step="0.01"
                value={formData.original_cost.toString()}
                onChange={handleNumberChange}
                error={errors.original_cost}
                fullWidth
                required
              />
              
              <Input
                label="Salvage Value ($)"
                name="salvage_value"
                type="number"
                min="0"
                step="0.01"
                value={formData.salvage_value.toString()}
                onChange={handleNumberChange}
                error={errors.salvage_value}
                fullWidth
              />

              <Input
                label="Disposal Cost ($)"
                name="disposal_cost"
                type="number"
                min="0"
                step="0.01"
                value={formData.disposal_cost.toString()}
                onChange={handleNumberChange}
                error={errors.disposal_cost}
                fullWidth
              />
            </div>
          </div>

          {/* Disposal Information */}
          <div>
            <h3 className="text-lg font-medium text-gray-900 mb-4">Disposal Information</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <Select
                label="Disposal Method"
                options={disposalMethodOptions}
                value={formData.disposal_method}
                onChange={handleSelectChange('disposal_method')}
                fullWidth
                required
              />
              
              <Select
                label="Environmental Impact"
                options={environmentalImpactOptions}
                value={formData.environmental_impact}
                onChange={handleSelectChange('environmental_impact')}
                fullWidth
                required
              />
            </div>
          </div>

          {errors.submit && (
            <div className="text-sm text-red-600 bg-red-50 p-2 rounded">
              {errors.submit}
            </div>
          )}
        </CardContent>
        <CardFooter className="flex justify-between">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/rd-waste')}
          >
            Cancel
          </Button>
          <Button
            type="submit"
            isLoading={isSubmitting}
          >
            {isEdit ? 'Update Waste Item' : 'Add Waste Item'}
          </Button>
        </CardFooter>
      </form>
      
      {/* Image Preview Modal */}
      {showImageModal && imagePreview && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="relative max-w-4xl max-h-full">
            <Button
              variant="secondary"
              className="absolute top-4 right-4 z-10"
              onClick={() => {
                setShowImageModal(false);
                setImagePreview(null);
              }}
            >
              <X className="h-4 w-4" />
            </Button>
            <img
              src={imagePreview}
              alt="Waste item preview"
              className="max-w-full max-h-full object-contain rounded-lg"
            />
          </div>
        </div>
      )}
    </Card>
  );
};

export default RDWasteForm;